import { apiFetcher, JsonValue } from '@gjensidige/service-async';
import {
  remoteLogFetchError,
  remoteLogMessage
} from '@gjensidige/service-error-handler';
import { stringifyQuery } from '@gjensidige/service-querystring';

import { ShortNameAndEntrance } from '../redux/slices/config';
import {
  AuthLevel,
  Bedrift,
  OpeningHours,
  QueueStatus,
  Token
} from '../types/api';
import { DirectEntrance } from '../types/async-config';
import { EventLabel } from './tracking-service';

async function gjeFetch<T extends JsonValue>(
  url: string
): Promise<T | undefined>;
async function gjeFetch<T extends JsonValue>(
  url: string,
  defaultValue: T
): Promise<T>;
async function gjeFetch<T extends JsonValue>(
  url: string,
  defaultValue?: T
): Promise<T | undefined> {
  const res = await apiFetcher.getJson<T>(url);
  if (res.isOk()) {
    return res.value;
  }

  void remoteLogFetchError(url, res.error);
  return defaultValue;
}

function uniqueWithContent(
  s: string | undefined,
  i: number,
  arr: string[]
): boolean {
  return !!s?.length && arr.indexOf(s) === i;
}

function uniqueWithContentShortNameAndEntrance(
  s: ShortNameAndEntrance | undefined,
  i: number,
  arr: ShortNameAndEntrance[]
): boolean {
  return !!(
    s &&
    s.shortName.length &&
    s.entrance?.length &&
    arr.findIndex(
      (value) =>
        value.shortName === s.shortName && value.entrance === s.entrance
    ) === i
  );
}

function createQueryParamQueueAndEntrance(
  shortNameAndEntrance: ShortNameAndEntrance | ShortNameAndEntrance[]
): string {
  if (Array.isArray(shortNameAndEntrance)) {
    return shortNameAndEntrance.map(createQueryParamQueueAndEntrance).join(',');
  }
  return shortNameAndEntrance.shortName + '-' + shortNameAndEntrance.entrance;
}

export interface CompanyAccess {
  name: string;
  id: string;
  partref: string;
  eventLabel: EventLabel;
}

function mask(value: string) {
  // Numbers are often part of risiko numbers or similar
  return value.replace(/\d+/g, '#');
}

export const apiService = {
  async isLoggedIn(): Promise<boolean> {
    const res = await gjeFetch<AuthLevel>('/api-tools/v1/open/auth-level', {
      value: 0
    });
    return res.value > 0;
  },

  async getCompanyAccesses(): Promise<CompanyAccess[]> {
    const res = await gjeFetch<Bedrift[]>(
      '/api-tilganger/v1/bedriftstilganger',
      []
    );

    return res
      .map(
        (tilgang) =>
          <CompanyAccess>{
            name: tilgang.navn,
            id: tilgang.id,
            partref: tilgang.partref,
            eventLabel: 'velg_bedrift'
          }
      )
      .sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
  },

  async getToken(): Promise<string | undefined> {
    const res = await gjeFetch<Token>('/api-token/telia');

    return res?.value;
  },

  async getOpeningHours(entrance: string): Promise<OpeningHours> {
    return (
      (await gjeFetch<OpeningHours>(
        `/api-kundeservice/open/callguide/v2/entrance/${entrance}/opening-hours`
      )) || []
    );
  },

  async getChatQueueAvailability(
    shortName: string | (string | undefined)[],
    checkNumberOfAgents = true
  ): Promise<QueueStatus> {
    const param =
      typeof shortName === 'string'
        ? shortName
        : shortName.filter(uniqueWithContent).join(',');

    let res = { available: false };
    if (param === 'stengt') {
      // master queue is this sometimes, should just default to available: false
      void remoteLogMessage("Queue was 'stengt'", {
        severity: 'INFO'
      });
    } else if (param.length <= 0) {
      void remoteLogMessage('No queue provided', {
        severity: 'WARN',
        extras: [
          {
            key: 'rawQueueValue',
            value: shortName.toString()
          },
          {
            key: 'pathname',
            value: mask(location.pathname)
          }
        ]
      });
    } else {
      res = await gjeFetch<QueueStatus>(
        `/api-kundeservice/open/callguide/v1/queue/${param}?checkNumberOfAgents=${checkNumberOfAgents}`,
        res
      );
    }

    window.CallGuideChatClient.api.isAvailable = res.available;
    return { available: res.available };
  },

  async getChatQueueEntranceAvailability(
    shortNameAndEntrance:
      | ShortNameAndEntrance
      | (ShortNameAndEntrance | undefined)[],
    checkNumberOfAgents = true
  ): Promise<QueueStatus> {
    const param = Array.isArray(shortNameAndEntrance)
      ? createQueryParamQueueAndEntrance(
          shortNameAndEntrance
            .filter(uniqueWithContentShortNameAndEntrance)
            .filter((value): value is ShortNameAndEntrance => !!value)
        )
      : createQueryParamQueueAndEntrance(shortNameAndEntrance);

    let res = { available: false };
    if (param === 'stengt') {
      // master queue is this sometimes, should just default to available: false
      void remoteLogMessage("Queue was 'stengt'", {
        severity: 'INFO'
      });
    } else if (param.length <= 0) {
      void remoteLogMessage('No queue provided', {
        severity: 'WARN',
        extras: [
          {
            key: 'rawQueueValue',
            value: shortNameAndEntrance.toString()
          },
          {
            key: 'pathname',
            value: mask(location.pathname)
          }
        ]
      });
    } else {
      res = await gjeFetch<QueueStatus>(
        `/api-kundeservice/open/callguide/v2/queue-and-entrance/is-available?checkNumberOfAgents=${checkNumberOfAgents}&queuesAndEntrances=${param}`,
        res
      );
    }

    window.CallGuideChatClient.api.isAvailable = res.available;
    return { available: res.available };
  },

  async getChatAvailabilityByEntrance(directEntrance: DirectEntrance) {
    const params: Record<string, string | number> = {
      queueName: directEntrance.shortName
    };

    // Forcing 5 queue length max
    params.maxQueueLength = 5;

    if (directEntrance.maxQueueLength) {
      params.maxQueueLength = directEntrance.maxQueueLength;
    }

    const res = await gjeFetch<QueueStatus>(
      stringifyQuery(
        params,
        `/api-kundeservice/open/callguide/v2/entrance/${directEntrance.entrance}`
      ),
      {
        available: false,
        queueTooLong: false
      }
    );

    window.CallGuideChatClient.api.isAvailable = res.available;
    return { available: res.available, queueTooLong: res.queueTooLong };
  }
};
